import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CircleCheck, CircleHelp, CircleSlash, CircleX, RefreshCw } from 'lucide-react';
import { salesApi } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queries';
import type { BulkResult, BulkTask, DocumentStatus } from '../../../api/types';
import type { Dictionary } from '../../../i18n/en';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { Callout } from '../../../ui/Feedback';
import { Code, Money } from '../../../ui/Layout';
import type { InvoiceListSearch } from '../InvoiceListPage';
import { taxAxisOf } from '../status';
import styles from './Batch.module.css';
import { BatchTable, type BatchColumn } from './BatchTable';
import type { BatchCriteria } from './criteria';
import { etaAnswerOf, reasonOf, rowState, type Outcome } from './rules';
import { reasonText } from './text';

/**
 * The results (BLOCK-1B-BULK-DESIGN §5, AC-B8): ONE table, a text outcome and a code on each row,
 * in the order fixed when the run ended; rows never move while the Tax Authority's answers arrive.
 * A retry marks its rows in place (`data-retry`, the «ما سيحدث» column) before its short dialog.
 */
export function ResultsPanel({
  orgId,
  task,
  results,
  order,
  lost,
  retryMarks,
  isBusy,
  criteria,
  onRetry,
  onSaveAgain,
  onNew,
}: {
  orgId: string;
  task: BulkTask;
  results: readonly BulkResult[];
  /** The fixed order of the rows (ids). */
  order: readonly string[];
  lost: boolean;
  /** During a retry: the retried rows and what will happen to each (else null). */
  retryMarks: ReadonlyMap<string, BulkResult> | null;
  isBusy: boolean;
  criteria: BatchCriteria;
  onRetry: () => void;
  onSaveAgain: () => void;
  onNew: () => void;
}) {
  const { t, locale, formatNumber } = useI18n();
  const byId = new Map(results.map((r) => [r.id, r]));
  const rows = order.flatMap((id) => {
    const r = byId.get(id);
    return r ? [r] : [];
  });
  const states = new Map(rows.map((r) => [r.id, rowState(task, r)]));
  const stateOf = (r: BulkResult) => states.get(r.id) ?? rowState(task, r);
  const count = (o: Outcome) => [...states.values()].filter((s) => s.outcome === o).length;
  const retryable = rows.filter((r) => stateOf(r).retryable);

  // The Tax Authority's answer on each refused row (WP-1b.2 verdict rows, DEC-022).
  const etaRows = rows.filter((r) => stateOf(r).etaAnswer);
  const etaQueries = useQueries({
    queries: etaRows.map((r) => ({
      queryKey: [...queryKeys.org(orgId), 'sales', 'eta', r.id],
      queryFn: ({ signal }: { signal: AbortSignal }) => salesApi.eta(orgId, r.id, signal),
      staleTime: 60_000,
    })),
  });
  const answers = new Map(
    etaRows.map((r, i) => {
      const data = etaQueries[i]?.data;
      if (!data) return [r.id, null] as const;
      const answer =
        etaAnswerOf(data.results, locale) ??
        ({ code: data.state.status, text: data.state.statusReason ?? null } as const);
      return [r.id, answer] as const;
    }),
  );

  const outcomeCell = (r: BulkResult) => {
    const state = stateOf(r);
    if (state.outcome === 'done')
      return (
        <span className={styles.outcomeDone}>
          <CircleCheck size={15} aria-hidden="true" />
          <span>{t.batch.results.done}</span>
        </span>
      );
    if (state.outcome === 'waiting')
      return (
        <span className={styles.outcomeWaiting}>
          <span className={styles.liveDot} aria-hidden="true" />
          <span>{t.sales.kpis.sending}</span>
        </span>
      );
    if (state.outcome === 'skipped')
      return (
        <span className={styles.outcomeSkipped}>
          <CircleSlash size={15} aria-hidden="true" />
          <span>
            {t.batch.results.skipped}
            {' — '}
            {reasonText(t, reasonOf(task, r))}
          </span>
        </span>
      );
    // The call's answer was lost and the document moved since, not to the task's end state (§6,
    // RIB-02): say so, without claiming either outcome.
    if (state.outcome === 'unconfirmed')
      return (
        <span className={styles.outcomeUnconfirmed}>
          <CircleHelp size={15} aria-hidden="true" />
          <span>{t.batch.results.unconfirmed(stateText(r))}</span>
        </span>
      );
    const answer = answers.get(r.id);
    const reason = state.etaAnswer
      ? t.batch.reasons.etaAnswer(answer ? (answer.text ?? answer.code) : '…')
      : reasonText(t, reasonOf(task, r));
    return (
      <span className={styles.outcomeFailed}>
        <CircleX size={15} aria-hidden="true" />
        <span>
          {t.batch.results.failed}
          {' — '}
          {reason}
        </span>
      </span>
    );
  };

  /** The document's stage and ETA state in words (a re-read row whose state moved). */
  const stateText = (r: BulkResult) => {
    if (!r.status) return '—';
    const axis = taxAxisOf({
      status: r.status as DocumentStatus,
      readiness: r.readiness ?? null,
      etaStatus: r.etaStatus ?? null,
    });
    return axis ? t.taxStatus[axis] : t.docStatus[r.status];
  };

  const codeOf = (r: BulkResult) => {
    const state = stateOf(r);
    if (state.etaAnswer) return answers.get(r.id)?.code ?? r.etaStatus ?? undefined;
    return r.ok ? undefined : r.code;
  };

  const columns: BatchColumn<BulkResult>[] = [
    {
      key: 'number',
      header: t.sales.columns.number,
      isPrimary: true,
      cell: (r) => (
        <span className={styles.mono} dir="ltr">
          {r.number ?? '—'}
        </span>
      ),
    },
    {
      key: 'buyer',
      header: t.sales.columns.customer,
      cell: (r) =>
        r.status ? (
          <UserText>{r.cash ? t.sales.cash : (r.receiverName ?? t.sales.noCustomer)}</UserText>
        ) : (
          '—'
        ),
    },
    {
      key: 'amount',
      header: t.sales.columns.amount,
      align: 'end',
      cell: (r) => <Money value={r.totalAmount} currency="EGP" />,
    },
    { key: 'outcome', header: t.batch.results.outcome, cell: outcomeCell },
    {
      key: 'code',
      header: t.batch.results.code,
      cell: (r) => {
        const code = codeOf(r);
        return code ? <Code>{code}</Code> : '—';
      },
    },
    ...(retryMarks
      ? [
          {
            key: 'retry',
            header: t.batch.review.outcome,
            cell: (r: BulkResult) => {
              const mark = retryMarks.get(r.id);
              if (!mark) return '—';
              return mark.ok
                ? t.batch.review.willRun
                : t.batch.review.willSkip(reasonText(t, reasonOf(task, mark)));
            },
          },
        ]
      : []),
    {
      key: 'next',
      header: t.batch.results.next,
      cell: (r) => {
        if (r.code === 'not_found' || !r.status) return '—';
        if (r.code === 'validation_error' && r.status === 'draft')
          return (
            <Link
              to="/orgs/$orgId/sales/invoices/$docId/edit"
              params={{ orgId, docId: r.id }}
              className={styles.textLink}
              data-testid="result-finish"
            >
              {t.batch.results.finishDraft}
            </Link>
          );
        return (
          <Link
            to="/orgs/$orgId/sales/invoices/$docId"
            params={{ orgId, docId: r.id }}
            className={styles.textLink}
            data-testid="result-open"
          >
            {t.batch.results.openDoc}
          </Link>
        );
      },
    },
  ];

  const done = count('done');
  const waiting = count('waiting');
  const skipped = count('skipped');
  const failed = count('failed');
  const unconfirmed = count('unconfirmed');
  return (
    <div className={styles.block} data-testid="batch-results">
      {lost ? (
        <div data-testid="batch-lost">
          <Callout tone="warning" role="note">
            {t.batch.results.lost}
          </Callout>
        </div>
      ) : null}
      <p
        className={styles.summary}
        role="status"
        data-testid="batch-results-summary"
        data-done={done}
        data-waiting={waiting}
        data-skipped={skipped}
        data-failed={failed}
        data-unconfirmed={unconfirmed}
      >
        {resultsSummary(t, { done, skipped, failed, waiting, unconfirmed }, formatNumber)}
      </p>
      {waiting ? <p className={styles.following}>{t.batch.results.following}</p> : null}
      <BatchTable<BulkResult>
        caption={t.batch.results.title}
        testId="batch-results-table"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        rowProps={(r) => {
          const mark = retryMarks?.get(r.id);
          return {
            'data-testid': `result-${r.number ?? r.id}`,
            'data-outcome': stateOf(r).outcome,
            'data-code': codeOf(r),
            'data-eta-status': r.etaStatus ?? undefined,
            'data-retry': mark ? (mark.ok ? 'run' : 'skip') : undefined,
          };
        }}
      />
      <div className={styles.actions}>
        {retryable.length ? (
          <Button
            variant="primary"
            icon={<RefreshCw size={16} aria-hidden="true" />}
            onPress={onRetry}
            isPending={isBusy}
            testId="batch-retry"
          >
            {t.batch.results.retry}
          </Button>
        ) : null}
        {task === 'pdf' && done ? (
          <Button
            variant="secondary"
            onPress={onSaveAgain}
            isDisabled={isBusy}
            testId="batch-save-again"
          >
            {t.batch.results.saveAgain}
          </Button>
        ) : null}
        <Button variant="secondary" onPress={onNew} isDisabled={isBusy} testId="batch-new">
          {t.batch.results.new}
        </Button>
        <Link
          to="/orgs/$orgId/sales/invoices"
          params={{ orgId }}
          search={criteria as InvoiceListSearch}
          className={styles.textLink}
          data-testid="batch-back-to-list"
        >
          {t.batch.results.back}
        </Link>
      </div>
    </div>
  );
}

/**
 * The results summary as sentences (AC-B4): the four counts, then — only when some answers were lost
 * and their documents moved — «لم تُؤكَّد n» as a term of its own, never counted as «لم يكتمل»
 * (RIB-02).
 */
export function resultsSummary(
  t: Dictionary,
  c: Record<Outcome, number>,
  formatNumber: (value: string) => string,
): string {
  const n = (v: number) => formatNumber(String(v));
  const main = t.batch.results.summary(n(c.done), n(c.skipped), n(c.failed), n(c.waiting));
  return c.unconfirmed ? `${main} ${t.batch.results.unconfirmedCount(n(c.unconfirmed))}` : main;
}
